import JSZip from "jszip";
import type { SlideInfo } from "./types";

/**
 * Parses slide metadata out of a .pptx in the browser.
 *
 * In v1 this ran server-side: the function downloaded the whole deck, unzipped
 * it, and regex-scraped the XML on every request — the heaviest CPU work in the
 * app, on a plan billed by execution time. It now runs on the client against
 * bytes it already needs for insertion, so the deck is downloaded once and the
 * server does none of this.
 *
 * Parsing uses DOMParser rather than regexes so that namespace prefixes,
 * attribute ordering and self-closing tags can't silently break title
 * extraction.
 */

const NS = {
  p: "http://schemas.openxmlformats.org/presentationml/2006/main",
  a: "http://schemas.openxmlformats.org/drawingml/2006/main",
  r: "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
  pkg: "http://schemas.openxmlformats.org/package/2006/relationships",
  p14: "http://schemas.microsoft.com/office/powerpoint/2010/main",
};

function parseXml(xml: string): Document {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Malformed XML in presentation");
  }
  return doc;
}

/** Concatenates all <a:t> runs inside an element, preserving paragraph order. */
function textOf(element: Element): string {
  const runs = element.getElementsByTagNameNS(NS.a, "t");
  let text = "";
  for (let i = 0; i < runs.length; i++) {
    text += runs[i]?.textContent ?? "";
  }
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Prefers the actual title placeholder over "first text on the slide", which is
 * what v1 used and which picked up stray labels and page numbers.
 */
function extractTitle(slideDoc: Document): string {
  const shapes = slideDoc.getElementsByTagNameNS(NS.p, "sp");

  for (let i = 0; i < shapes.length; i++) {
    const shape = shapes[i];
    if (!shape) continue;

    const placeholders = shape.getElementsByTagNameNS(NS.p, "ph");
    const placeholder = placeholders[0];
    if (!placeholder) continue;

    const type = placeholder.getAttribute("type");
    if (type === "title" || type === "ctrTitle") {
      const text = textOf(shape);
      if (text) return text;
    }
  }

  // Fallback: first non-empty text run anywhere on the slide.
  for (let i = 0; i < shapes.length; i++) {
    const shape = shapes[i];
    if (!shape) continue;
    const text = textOf(shape);
    if (text) return text;
  }

  return "";
}

/**
 * PowerPoint's insertSlidesFromBase64 expects sourceSlideIds shaped as
 * "<slideId>#<creationId>". v1 emitted "<slideId>#" with an empty creationId,
 * which relies on undocumented host tolerance. We read the real creationId from
 * the slide's extension list.
 */
function extractCreationId(slideDoc: Document): string {
  const nodes = slideDoc.getElementsByTagNameNS(NS.p14, "creationId");
  return nodes[0]?.getAttribute("val") ?? "";
}

function resolveTarget(target: string): string {
  if (target.startsWith("/")) return target.replace(/^\/+/, "");
  return `ppt/${target}`.replace(/\/{2,}/g, "/");
}

export async function parseSlides(pptxBytes: ArrayBuffer): Promise<SlideInfo[]> {
  const zip = await JSZip.loadAsync(pptxBytes);

  const presentationFile = zip.file("ppt/presentation.xml");
  const relsFile = zip.file("ppt/_rels/presentation.xml.rels");

  if (!presentationFile || !relsFile) {
    throw new Error("Not a valid PowerPoint file: missing presentation metadata");
  }

  const [presentationXml, relsXml] = await Promise.all([
    presentationFile.async("string"),
    relsFile.async("string"),
  ]);

  const relDoc = parseXml(relsXml);
  const relMap = new Map<string, string>();

  const relationships = relDoc.getElementsByTagNameNS(NS.pkg, "Relationship");
  for (let i = 0; i < relationships.length; i++) {
    const rel = relationships[i];
    if (!rel) continue;
    const id = rel.getAttribute("Id");
    const target = rel.getAttribute("Target");
    if (id && target) relMap.set(id, resolveTarget(target));
  }

  const presentationDoc = parseXml(presentationXml);
  const slideIdNodes = presentationDoc.getElementsByTagNameNS(NS.p, "sldId");

  // Read every slide's XML concurrently. Sequential awaits here were a
  // meaningful part of v1's latency on large decks.
  const tasks: Array<Promise<SlideInfo | null>> = [];

  for (let i = 0; i < slideIdNodes.length; i++) {
    const node = slideIdNodes[i];
    if (!node) continue;

    const slideId = node.getAttribute("id");
    const relId = node.getAttributeNS(NS.r, "id") ?? node.getAttribute("r:id");
    if (!slideId || !relId) continue;

    const slideNumber = tasks.length + 1;
    const slidePath = relMap.get(relId);

    tasks.push(
      (async (): Promise<SlideInfo> => {
        let title = "";
        let creationId = "";

        const file = slidePath ? zip.file(slidePath) : null;
        if (file) {
          try {
            const slideDoc = parseXml(await file.async("string"));
            title = extractTitle(slideDoc);
            creationId = extractCreationId(slideDoc);
          } catch {
            // A single unparseable slide shouldn't fail the whole deck.
          }
        }

        return {
          slideNumber,
          sourceSlideId: `${slideId}#${creationId}`,
          title: title || `Slide ${slideNumber}`,
        };
      })()
    );
  }

  const slides = await Promise.all(tasks);
  return slides.filter((slide): slide is SlideInfo => slide !== null);
}

/**
 * Converts the deck to base64 for insertSlidesFromBase64.
 *
 * Uses FileReader on a Blob rather than a manual String.fromCharCode loop:
 * the encoding happens in native code off the JS heap, which matters because a
 * large deck otherwise builds a very large intermediate string.
 */
export function toBase64(bytes: ArrayBuffer): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Base64 encoding failed"));
    reader.readAsDataURL(new Blob([bytes]));
  });
}
